import React, { useEffect, useState } from "react";
import { Box, Button, Stack, IconButton, Tooltip, Chip } from "@mui/material";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import GlobalWrapper from "../../../components/Wrapper/GlobalWrapper";
import GlobalDatagrid from "../../../components/mui/DataGrid/GlobalDatagrid";
import Iconify from "../../../components/color-utils/Iconify";
import { Career_Job_Table_Head } from "../../../components/datagrid/career/CareerJobTableHead";
import { getJobs } from "../../../redux/features/careerSlice";
import PostJobDialog from "./PostJobDialog";
import JobStatusActions from "./JobStatusActions";
import StudentPreviewDialog from "./StudentPreviewDialog";

// Career Connect — officer's list of posted jobs.
// Uses the shared server-side pagination (globalDataGrid slice) exactly like
// the other admin modules. The "Post a Job" button sits in the wrapper actions.
const CareerJobList = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { paginationData } = useSelector((state) => state.globalDataGrid);
  const { loading, jobData } = useSelector((state) => state.career);
  const { gridSize, pageNum, search } = paginationData;

  // controlled edit dialog
  const [editJobId, setEditJobId] = useState(null);
  const [editOpen, setEditOpen] = useState(false);

  // student-preview dialog
  const [previewJobId, setPreviewJobId] = useState(null);
  const [previewOpen, setPreviewOpen] = useState(false);

  const data = {
    page: pageNum,
    limit: gridSize,
    filter: search,
  };

  const refresh = () => dispatch(getJobs(data));

  useEffect(() => {
    dispatch(getJobs(data));
    // re-fetch when the page, page size, or search text changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pageNum, gridSize, search]);

  // data columns (drop the read-only status text — replaced by the editable dropdown below);
  // make the job title a clickable link to the job detail page
  const dataColumns = Career_Job_Table_Head.filter((c) => c.field !== "job_status").map((c) =>
    c.field === "job_title"
      ? {
          ...c,
          renderCell: (params) => (
            <span
              style={{ color: "#2f72b3", cursor: "pointer", fontWeight: 600 }}
              onClick={(e) => {
                e.stopPropagation();
                navigate(`/career-connect/jobs/${params.row.career_job_id}`);
              }}
            >
              {params.row.job_title}
            </span>
          ),
        }
      : c
  );

  const columns = [
    ...dataColumns,
    {
      field: "status_edit",
      headerName: "Status",
      width: 150,
      sortable: false,
      filterable: false,
      // editable status dropdown (Draft / Published / Closed / Delete)
      renderCell: (params) => <JobStatusActions job={params.row} onDone={refresh} />,
    },
    {
      field: "edit",
      headerName: "Edit",
      width: 70,
      sortable: false,
      filterable: false,
      renderCell: (params) => (
        <Tooltip title="Edit job">
          <IconButton
            size="small"
            onClick={(e) => {
              e.stopPropagation();
              setEditJobId(params.row.career_job_id);
              setEditOpen(true);
            }}
          >
            <Iconify icon="eva:edit-2-outline" />
          </IconButton>
        </Tooltip>
      ),
    },
    {
      field: "preview",
      headerName: "Preview",
      width: 110,
      sortable: false,
      filterable: false,
      // see the job as a student would see it on the student portal
      renderCell: (params) => (
        <Button
          size="small"
          variant="outlined"
          startIcon={<Iconify icon="eva:eye-outline" />}
          onClick={(e) => {
            e.stopPropagation();
            setPreviewJobId(params.row.career_job_id);
            setPreviewOpen(true);
          }}
        >
          Preview
        </Button>
      ),
    },
    {
      field: "students",
      headerName: "Students",
      width: 300,
      sortable: false,
      filterable: false,
      renderCell: (params) => {
        const jobId = params.row.career_job_id;
        return (
          <Stack direction="row" spacing={1} sx={{ flexWrap: "wrap", gap: 1 }}>
            {/* who is eligible to apply (everyone in the targeted batch) */}
            <Button
              size="small"
              variant="outlined"
              startIcon={<Iconify icon="eva:people-outline" />}
              onClick={(e) => {
                e.stopPropagation();
                navigate(`/career-connect/jobs/${jobId}/eligible-students`);
              }}
            >
              Who Can Apply
            </Button>
            {/* who actually applied */}
            <Button
              size="small"
              variant="outlined"
              startIcon={<Iconify icon="eva:person-done-outline" />}
              onClick={(e) => {
                e.stopPropagation();
                navigate(`/career-connect/jobs/${jobId}/applicants`);
              }}
            >
              Applicants
            </Button>
          </Stack>
        );
      },
    },
  ];

  // "Updates" = total unread updates across a job's applicants (student messages +
  // activity since the officer last opened each application). Shown right after the
  // "Applicants" column so the officer sees at a glance which jobs need attention.
  const updatesColumn = {
    field: "updates",
    headerName: "Updates",
    width: 110,
    sortable: false,
    filterable: false,
    renderCell: (params) => {
      const n = Number(params.row.updates_count) || 0;
      return n > 0 ? (
        <Tooltip title="New student messages / activity across this job's applicants">
          <Chip size="small" color="error" label={`${n} new`} />
        </Tooltip>
      ) : (
        <span style={{ color: "#9aa5b1" }}>0</span>
      );
    },
  };
  const applicantsIdx = columns.findIndex((c) => c.field === "applicant_count");
  columns.splice(applicantsIdx >= 0 ? applicantsIdx + 1 : columns.length, 0, updatesColumn);

  return (
    <GlobalWrapper
      title={"Career Connect Jobs"}
      actions={
        <Stack direction="row" spacing={1} alignItems="center">
          {/* the places jobs are posted in: rename one, retire an unused one */}
          <Button
            size="medium"
            variant="outlined"
            startIcon={<Iconify icon="eva:pin-outline" />}
            onClick={() => navigate("/career-connect/locations")}
          >
            Locations
          </Button>
          <PostJobDialog dispatchParams={data} />
        </Stack>
      }
    >
      <Box sx={{ width: "100%", padding: 0 }}>
        <GlobalDatagrid
          tableHead={columns}
          rows={jobData?.data || []}
          meta={jobData?.meta?.totalRecords || 0}
          loading={loading}
          rowUniqueId="career_job_id"
          rowClick={false}
        />
      </Box>

      {/* controlled edit dialog (reuses the Post-Job dialog in edit mode) */}
      <PostJobDialog
        editJobId={editJobId}
        open={editOpen}
        setOpen={setEditOpen}
        hideTrigger
        dispatchParams={data}
      />

      {/* student-perspective preview of a job */}
      <StudentPreviewDialog jobId={previewJobId} open={previewOpen} setOpen={setPreviewOpen} />
    </GlobalWrapper>
  );
};

export default CareerJobList;
